import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { ServicesBaseUrl } from '@garmin-avcloud/avcloud-fly-web-common/_shared';
import { FLY_WEB_COMMON_CONFIG } from '@garmin-avcloud/avcloud-fly-web-common/config';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class PdiDomainConfiguration {
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default bearerAuth credential
    if (!this.credentials['bearerAuth']) {
      this.credentials['bearerAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link PdiDomainConfiguration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link PdiDomainConfiguration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AllowListControllerService = /*#__PURE__*/(() => {
  class AllowListControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllowListFileAircraft(identifier, aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (identifier === null || identifier === undefined) {
        throw new Error('Required parameter identifier was null or undefined when calling getAllowListFileAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let localVarPath = `/v2-0/allow-list/aircraft/${this.configuration.encodeParam({
        name: "identifier",
        value: identifier,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/file`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllowListSFFAircraft(allowListSffSearch, observe = 'body', reportProgress = false, options) {
      if (allowListSffSearch === null || allowListSffSearch === undefined) {
        throw new Error('Required parameter allowListSffSearch was null or undefined when calling getAllowListSFFAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/allow-list/aircraft/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: allowListSffSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLatestAllowListRefData(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/v2-0/allow-list/latest`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AllowListControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AllowListControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AllowListControllerService,
        factory: AllowListControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AllowListControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CabinBrieferControllerService = /*#__PURE__*/(() => {
  class CabinBrieferControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getCabinBrieferFileCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getCabinBrieferFileCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cabin-briefer/customer/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCabinBrieferFileOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getCabinBrieferFileOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cabin-briefer/oem/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCabinBrieferSFFCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getCabinBrieferSFFCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cabin-briefer/customer/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCabinBrieferSFFOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getCabinBrieferSFFOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cabin-briefer/oem/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CabinBrieferControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CabinBrieferControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CabinBrieferControllerService,
        factory: CabinBrieferControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CabinBrieferControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ChecklistControllerService = /*#__PURE__*/(() => {
  class ChecklistControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getChecklistFileCustomer(checklistSearch, observe = 'body', reportProgress = false, options) {
      if (checklistSearch === null || checklistSearch === undefined) {
        throw new Error('Required parameter checklistSearch was null or undefined when calling getChecklistFileCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/checklist/customer/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checklistSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChecklistFileOem(checklistOemSearch, observe = 'body', reportProgress = false, options) {
      if (checklistOemSearch === null || checklistOemSearch === undefined) {
        throw new Error('Required parameter checklistOemSearch was null or undefined when calling getChecklistFileOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/checklist/oem/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checklistOemSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChecklistSffCustomer(checklistSearch, observe = 'body', reportProgress = false, options) {
      if (checklistSearch === null || checklistSearch === undefined) {
        throw new Error('Required parameter checklistSearch was null or undefined when calling getChecklistSffCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/checklist/customer/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checklistSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChecklistSffOem(checklistOemSearch, observe = 'body', reportProgress = false, options) {
      if (checklistOemSearch === null || checklistOemSearch === undefined) {
        throw new Error('Required parameter checklistOemSearch was null or undefined when calling getChecklistSffOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/checklist/oem/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checklistOemSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ChecklistControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChecklistControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChecklistControllerService,
        factory: ChecklistControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChecklistControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CmcConfigControllerService = /*#__PURE__*/(() => {
  class CmcConfigControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getCmcConfigFileCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getCmcConfigFileCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/customer/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCmcConfigFileHashAircraft(aircraftIdentifierSearch, maxSize, aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getCmcConfigFileHashAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (maxSize !== undefined && maxSize !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, maxSize, 'maxSize');
      }
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/aircraft/file-hash`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        params: localVarQueryParameters,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCmcConfigFileOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getCmcConfigFileOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/oem/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCmcConfigSFFAircraft(cmcConfigSffSearch, observe = 'body', reportProgress = false, options) {
      if (cmcConfigSffSearch === null || cmcConfigSffSearch === undefined) {
        throw new Error('Required parameter cmcConfigSffSearch was null or undefined when calling getCmcConfigSFFAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/aircraft/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: cmcConfigSffSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCmcConfigSFFCustomer(cmcConfigSffSearch, observe = 'body', reportProgress = false, options) {
      if (cmcConfigSffSearch === null || cmcConfigSffSearch === undefined) {
        throw new Error('Required parameter cmcConfigSffSearch was null or undefined when calling getCmcConfigSFFCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/customer/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: cmcConfigSffSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCmcConfigSFFOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getCmcConfigSFFOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/cmc-config/oem/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CmcConfigControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CmcConfigControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CmcConfigControllerService,
        factory: CmcConfigControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CmcConfigControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ElectronicDocumentControllerService = /*#__PURE__*/(() => {
  class ElectronicDocumentControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getElectronicDocumentFileCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getElectronicDocumentFileCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/electronic-document/customer/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getElectronicDocumentFileOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getElectronicDocumentFileOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/electronic-document/oem/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getElectronicDocumentSffCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getElectronicDocumentSffCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/electronic-document/customer/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getElectronicDocumentSffOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getElectronicDocumentSffOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/electronic-document/oem/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ElectronicDocumentControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ElectronicDocumentControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ElectronicDocumentControllerService,
        factory: ElectronicDocumentControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ElectronicDocumentControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemDiagnosticsControllerService = /*#__PURE__*/(() => {
  class OemDiagnosticsControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/pdiDomain';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new PdiDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getOemDiagnosticsFileCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getOemDiagnosticsFileCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/oem-diagnostics/customer/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOemDiagnosticsFileOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getOemDiagnosticsFileOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/oem-diagnostics/oem/file`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOemDiagnosticsFileSFFCustomer(aircraftIdentifierSearch, observe = 'body', reportProgress = false, options) {
      if (aircraftIdentifierSearch === null || aircraftIdentifierSearch === undefined) {
        throw new Error('Required parameter aircraftIdentifierSearch was null or undefined when calling getOemDiagnosticsFileSFFCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/oem-diagnostics/customer/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftIdentifierSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOemDiagnosticsFileSffOem(systemTypeSearch, observe = 'body', reportProgress = false, options) {
      if (systemTypeSearch === null || systemTypeSearch === undefined) {
        throw new Error('Required parameter systemTypeSearch was null or undefined when calling getOemDiagnosticsFileSffOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/octet-stream', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let localVarPath = `/v2-0/oem-diagnostics/oem/sff`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemTypeSearch,
        responseType: "blob",
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemDiagnosticsControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemDiagnosticsControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(PdiDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemDiagnosticsControllerService,
        factory: OemDiagnosticsControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemDiagnosticsControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const APIS = [AllowListControllerService, CabinBrieferControllerService, ChecklistControllerService, CmcConfigControllerService, ElectronicDocumentControllerService, OemDiagnosticsControllerService];

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/**
 * Type of checklist(OEM vs OPERATOR)
 */
var ChecklistType = /*#__PURE__*/function (ChecklistType) {
  ChecklistType["OEM"] = "OEM";
  ChecklistType["OPERATOR"] = "OPERATOR";
  return ChecklistType;
}(ChecklistType || {});
/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/**
 * The type of PDI file
 */
var PdiFileType = /*#__PURE__*/function (PdiFileType) {
  PdiFileType["UNKNOWN"] = "UNKNOWN";
  PdiFileType["ALLOW_LIST"] = "ALLOW_LIST";
  PdiFileType["CHECKLIST"] = "CHECKLIST";
  PdiFileType["CMC_CONFIG"] = "CMC_CONFIG";
  PdiFileType["CABIN_BRIEFER"] = "CABIN_BRIEFER";
  PdiFileType["OEM_DIAGNOSTICS"] = "OEM_DIAGNOSTICS";
  PdiFileType["ELECTRONIC_DOCUMENT"] = "ELECTRONIC_DOCUMENT";
  return PdiFileType;
}(PdiFileType || {});
/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * PdiDomain
 * project for building pdi jdbc data services
 *
 * The version of the OpenAPI document: 1.28.0-SNAPSHOT
 * Contact: __avn_pg_globalSvcs_eng_all_admin@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/**
 * data will correspond to GIFD2 or GIFD3 system
 */
var SystemType = /*#__PURE__*/function (SystemType) {
  SystemType["GIFD2"] = "GIFD2";
  SystemType["GIFD3"] = "GIFD3";
  return SystemType;
}(SystemType || {});
let PdiDomainApiModule = /*#__PURE__*/(() => {
  class PdiDomainApiModule {
    static forRoot(configurationFactory) {
      return {
        ngModule: PdiDomainApiModule,
        providers: [{
          provide: PdiDomainConfiguration,
          useFactory: configurationFactory
        }]
      };
    }
    constructor(parentModule, http) {
      if (parentModule) {
        throw new Error('PdiDomainApiModule is already loaded. Import in your base AppModule only.');
      }
      if (!http) {
        throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
      }
    }
    static {
      this.ɵfac = function PdiDomainApiModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PdiDomainApiModule)(i0.ɵɵinject(PdiDomainApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: PdiDomainApiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return PdiDomainApiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PdiDomainV2Module = /*#__PURE__*/(() => {
  class PdiDomainV2Module {
    static {
      this.ɵfac = function PdiDomainV2Module_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PdiDomainV2Module)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: PdiDomainV2Module
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [{
          ngModule: PdiDomainApiModule,
          providers: [{
            provide: PdiDomainConfiguration,
            deps: [FLY_WEB_COMMON_CONFIG],
            useFactory: config => new PdiDomainConfiguration({
              basePath: `${ServicesBaseUrl[config.environment]}/pdiDomain`
            })
          }]
        }]
      });
    }
  }
  return PdiDomainV2Module;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AllowListControllerService, BASE_PATH, COLLECTION_FORMATS, CabinBrieferControllerService, ChecklistControllerService, ChecklistType, CmcConfigControllerService, ElectronicDocumentControllerService, OemDiagnosticsControllerService, PdiDomainApiModule, PdiDomainConfiguration, PdiDomainV2Module, PdiFileType, SystemType };
